import { z } from 'zod';

/**
 * Mesh environments, ordered by how much damage a mistake does.
 *
 * This value drives the always-visible environment badge and the persistent
 * production hairline. An operator who believes they are in staging while
 * pointed at production can corrupt a live season, so the value is validated
 * at module load and the app refuses to boot without it. Defaulting it would
 * be actively dangerous — a missing variable must fail loudly, not silently
 * resolve to something that looks safe.
 */
export const MESH_ENVIRONMENTS = ['production', 'staging', 'dev'] as const;
export type MeshEnvironment = (typeof MESH_ENVIRONMENTS)[number];

const envSchema = z.object({
  VITE_ADMIN_API_URL: z.url({
    error: 'VITE_ADMIN_API_URL must be an absolute URL, e.g. http://localhost:4300',
  }),
  /**
   * Optional per-environment targets. Setting more than one turns the environment
   * badge into a switcher; a build with only `VITE_ADMIN_API_URL` set behaves
   * exactly as a single-target build always has, with the badge inert.
   *
   * Each API enforces CORS against exactly one console origin
   * (`ADMIN_UI_ORIGIN` in mesh-admin-api), so every environment listed here must
   * name this console as its allowed origin or the requests fail at the preflight.
   */
  VITE_ADMIN_API_URL_DEV: z.url().optional(),
  VITE_ADMIN_API_URL_STAGING: z.url().optional(),
  VITE_ADMIN_API_URL_PRODUCTION: z.url().optional(),
  VITE_MESH_ENV: z.enum(MESH_ENVIRONMENTS, {
    error: `VITE_MESH_ENV must be one of: ${MESH_ENVIRONMENTS.join(' | ')}`,
  }),
  VITE_USE_MOCKS: z
    .enum(['true', 'false'])
    .default('false')
    .transform((value) => value === 'true'),
  /**
   * CDN base for shop art thumbnails (ZazzBackend's SHOP_CDN_BASE_URL). Display
   * only — upload paths are always computed server-side via presign. Optional:
   * unset renders honest "no preview" placeholders rather than broken images.
   */
  VITE_SHOP_CDN_BASE: z.url().optional(),
});

function readEnv() {
  const parsed = envSchema.safeParse(import.meta.env);

  if (!parsed.success) {
    const detail = parsed.error.issues
      .map((issue) => `  - ${issue.path.join('.')}: ${issue.message}`)
      .join('\n');

    throw new Error(
      `Mesh Admin Console cannot start — invalid environment configuration:\n${detail}\n\n` +
        'Copy .env.example to .env and fill in the values.',
    );
  }

  return parsed.data;
}

const parsedEnv = readEnv();

const stripTrailingSlashes = (url: string): string => url.replace(/\/+$/, '');

/**
 * The environment selected when nothing has been chosen at runtime, and the one
 * `VITE_ADMIN_API_URL` describes. Always a configured target.
 */
export const DEFAULT_ENVIRONMENT: MeshEnvironment = parsedEnv.VITE_MESH_ENV;

const EXPLICIT_TARGETS: Record<MeshEnvironment, string | undefined> = {
  production: parsedEnv.VITE_ADMIN_API_URL_PRODUCTION,
  staging: parsedEnv.VITE_ADMIN_API_URL_STAGING,
  dev: parsedEnv.VITE_ADMIN_API_URL_DEV,
};

/**
 * API base URL per environment, or null where this build has no target.
 *
 * `VITE_ADMIN_API_URL` supplies the default environment's URL, so a build that
 * sets nothing else still has exactly one reachable target.
 */
export const ENVIRONMENT_TARGETS: Readonly<Record<MeshEnvironment, string | null>> =
  Object.fromEntries(
    MESH_ENVIRONMENTS.map((environment) => {
      const url =
        EXPLICIT_TARGETS[environment] ??
        (environment === DEFAULT_ENVIRONMENT ? parsedEnv.VITE_ADMIN_API_URL : undefined);
      return [environment, url === undefined ? null : stripTrailingSlashes(url)];
    }),
  ) as Record<MeshEnvironment, string | null>;

/** Environments this build can actually reach, in ascending order of safety. */
export const CONFIGURED_ENVIRONMENTS: readonly MeshEnvironment[] = MESH_ENVIRONMENTS.filter(
  (environment) => ENVIRONMENT_TARGETS[environment] !== null,
);

export const env = {
  shopCdnBase: parsedEnv.VITE_SHOP_CDN_BASE?.replace(/\/+$/, '') ?? null,
  /**
   * Whether the operator asked for mocks. Whether they actually run is decided
   * against the *active* environment in `@/lib/environment` — the rule that
   * matters is "never fabricate data while pointed at production", and the
   * active environment is chosen at runtime, not at build time.
   */
  useMocksRequested: parsedEnv.VITE_USE_MOCKS,
} as const;
